import { Stack } from 'components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { NoteForm } from '../NoteForm';
import { useNoteFormContext } from '../context';

type NoteFieldsProps = {
  expanded?: boolean;
  includeMpiEntry?: boolean;
  forceExpandedForMpi?: boolean;
};

export const NoteFields = ({
  expanded = true,
  includeMpiEntry = false,
  forceExpandedForMpi = false
}: NoteFieldsProps) => {
  const { enableDateTimeUpdates, enableMpiUpdate } = useFlags();
  const { isManagerReview, disabled } = useNoteFormContext();
  const showMpiEntry = includeMpiEntry && enableDateTimeUpdates && enableMpiUpdate;

  return (
    <NoteForm.FieldsContainer expanded={forceExpandedForMpi && showMpiEntry ? true : expanded}>
      {showMpiEntry && (
        <NoteForm.FieldsContainer.HalfRow>
          <NoteForm.AiScribeButton />
        </NoteForm.FieldsContainer.HalfRow>
      )}
      {showMpiEntry && (
        <NoteForm.FieldsContainer.HalfRow>
          <NoteForm.Fields.Note disabled={disabled} />
        </NoteForm.FieldsContainer.HalfRow>
      )}
      <NoteForm.FieldsContainer.Row>
        <NoteForm.FieldsContainer.HalfRow>
          <NoteForm.Fields.NoteType disabled={disabled} />
        </NoteForm.FieldsContainer.HalfRow>
        <NoteForm.FieldsContainer.HalfRow>
          <NoteForm.Fields.DateOfEncounter disabled={disabled} />
        </NoteForm.FieldsContainer.HalfRow>
      </NoteForm.FieldsContainer.Row>

      {enableDateTimeUpdates ? (
        <NoteForm.FieldsContainer.Row>
          <NoteForm.FieldsContainer.HalfRow>
            <NoteForm.Fields.StartTime disabled={disabled} />
          </NoteForm.FieldsContainer.HalfRow>
          <NoteForm.FieldsContainer.HalfRow>
            <NoteForm.Fields.TimeSpent disabled={disabled} />
          </NoteForm.FieldsContainer.HalfRow>
        </NoteForm.FieldsContainer.Row>
      ) : (
        !isManagerReview && (
          <NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.HalfRow>
              <NoteForm.Fields.StartTime disabled={disabled} />
            </NoteForm.FieldsContainer.HalfRow>
            <NoteForm.FieldsContainer.HalfRow>
              <NoteForm.Fields.TimeSpent disabled={disabled} />
            </NoteForm.FieldsContainer.HalfRow>
          </NoteForm.FieldsContainer.Row>
        )
      )}

      {isManagerReview && (
        <>
          {enableDateTimeUpdates ? (
            <>
              <NoteForm.FieldsContainer.Row>
                <NoteForm.FieldsContainer.HalfRow>
                  <NoteForm.Fields.Manager disabled={disabled} />
                </NoteForm.FieldsContainer.HalfRow>
                <NoteForm.FieldsContainer.HalfRow>
                  <NoteForm.Fields.Chw disabled={disabled} />
                </NoteForm.FieldsContainer.HalfRow>
              </NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.Row>
                <NoteForm.FieldsContainer.FullWidth>
                  <NoteForm.Fields.Provider disabled={disabled} />
                </NoteForm.FieldsContainer.FullWidth>
              </NoteForm.FieldsContainer.Row>
            </>
          ) : (
            <>
              <NoteForm.FieldsContainer.Row>
                <NoteForm.FieldsContainer.HalfRow>
                  <NoteForm.Fields.TimeSpent disabled={disabled} />
                </NoteForm.FieldsContainer.HalfRow>
                <NoteForm.FieldsContainer.HalfRow>
                  <NoteForm.Fields.Manager disabled={disabled} />
                </NoteForm.FieldsContainer.HalfRow>
              </NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.Row>
                <NoteForm.FieldsContainer.HalfRow>
                  <NoteForm.Fields.Chw disabled={disabled} />
                </NoteForm.FieldsContainer.HalfRow>
                <NoteForm.FieldsContainer.HalfRow>
                  <NoteForm.Fields.Provider disabled={disabled} />
                </NoteForm.FieldsContainer.HalfRow>
              </NoteForm.FieldsContainer.Row>
            </>
          )}
        </>
      )}

      {!isManagerReview && (
        <>
          <NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.HalfRow>
              <NoteForm.Fields.NextContact disabled={disabled} />
            </NoteForm.FieldsContainer.HalfRow>
          </NoteForm.FieldsContainer.Row>
          <NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.HalfRow>
              <NoteForm.Fields.WhoDidYouReach disabled={disabled} />
            </NoteForm.FieldsContainer.HalfRow>
          </NoteForm.FieldsContainer.Row>
          <NoteForm.FieldsContainer.FullWidth>
            <NoteForm.Fields.TypeOfSupport disabled={disabled} />
          </NoteForm.FieldsContainer.FullWidth>
        </>
      )}
    </NoteForm.FieldsContainer>
  );
};

export const NoteBodyFields = ({ hideNoteWhenMpi = false }: { hideNoteWhenMpi?: boolean }) => {
  const { enableDateTimeUpdates, enableMpiUpdate } = useFlags();
  const { disabled } = useNoteFormContext();
  const shouldHideNote = hideNoteWhenMpi && enableDateTimeUpdates && enableMpiUpdate;

  return (
    <>
      {!shouldHideNote && (
        <NoteForm.FieldsContainer.HalfRow>
          <NoteForm.Fields.Note disabled={disabled} />
        </NoteForm.FieldsContainer.HalfRow>
      )}
      <NoteForm.FieldsContainer.HalfRow>
        <NoteForm.Fields.DocumentationTime disabled={disabled} />
      </NoteForm.FieldsContainer.HalfRow>
    </>
  );
};

export const AiScribeMedia = () => {
  return (
    <>
      <NoteForm.AiScribe.Status />
      <NoteForm.AiScribe.AudioPlayer />
    </>
  );
};

export const DraftActions = () => {
  return (
    <NoteForm.Actions>
      <NoteForm.Actions.Left>
        <NoteForm.DeleteButton />
      </NoteForm.Actions.Left>
      <NoteForm.Actions.Right>
        <NoteForm.AutosaveIndicator />
        <NoteForm.SubmitButton>Final</NoteForm.SubmitButton>
      </NoteForm.Actions.Right>
    </NoteForm.Actions>
  );
};

export const PopupDraftActions = ({ onEdit }: { onEdit?: () => void }) => {
  return (
    <NoteForm.Actions>
      <NoteForm.Actions.Left>
        <NoteForm.DeleteButton />
        <NoteForm.EditButton onClick={onEdit} />
      </NoteForm.Actions.Left>
      <NoteForm.Actions.Right>
        <NoteForm.AutosaveIndicator />
        <NoteForm.SubmitButton>Final</NoteForm.SubmitButton>
      </NoteForm.Actions.Right>
    </NoteForm.Actions>
  );
};

export const FinalizedEditActions = ({ onCancel }: { onCancel?: () => void }) => {
  return (
    <NoteForm.Actions>
      <div></div>
      <NoteForm.Actions.Right>
        <Stack display="flex" gap="12px" flexDirection="row" alignItems="center">
          <Stack width="124px">
            <NoteForm.CancelButton onClick={onCancel}>Cancel</NoteForm.CancelButton>
          </Stack>
          <Stack width="124px">
            <NoteForm.SubmitButton>Final</NoteForm.SubmitButton>
          </Stack>
        </Stack>
      </NoteForm.Actions.Right>
    </NoteForm.Actions>
  );
};
